import {
  documentUploadsModule,
  FailedUpload,
  selFailedUploads,
} from '@features/document-uploads'
import { ConnectedWizardComponent } from '~/features/formation-viewer/formation-wizard/connected-wizard-component'
import {
  selFolderState,
  selEvalFolderStateExpr,
  selUploadedDocuments,
  selMembershipSide
} from '@features/st-folder-viewer/st-folder-selectors'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  Dialog,
  DialogButtons,
  Modal,
  PostItNote,
  QuestionnaireBanner,
  QuestionnaireFooter,
  QuestionnaireHeader,
  QuestionnairePageContainer,
  QuestionnaireProgressBar,
  QuestionnaireSectionTab
} from '@st/theme'
import { Progress } from '@st/util/progress'
import { SimpleHtmlMarkdown } from '@util/simple-html-markdown'
import { useState } from 'react'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { ClientUploadDocumentsCard } from './client-upload-documents-card'
import {
  selQuestionnaireCurrentPage,
  selQuestionnaireNavState,
  selQuestionnairePersonalizedNote,
  selQuestionnaireProgress,
  selQuestionnaireTabs,
  selQuestionnaireValidationResult
} from '@features/st-folder-viewer/st-folder-questionnaire'
import { STQuestionnaireTrailingHeader } from './st-questionnaire-trailing-header'
import { formatValidationErrors, QuestionnairePageValidationResult } from './st-questionnaire-validation'

type QuestionnaireAction = { type: 'viewAttachments' } | { type: 'confirmCompleteQuestionnaire' }

export function STQuestionnairePage() {
  const stFolder = useProcess('stFolder')
  const folderState = useSelect('stFolder', selFolderState)
  const currentPage = useSelect('stFolder', (s) => {
    if (s.status != 'ready' || s.questionnaire.status != 'ready') return undefined
    return selQuestionnaireCurrentPage(s.questionnaire, { stFolder: s })
  })
  const navState = useSelect('stFolder', (s) => {
    if (s.status != 'ready' || s.questionnaire.status != 'ready') return undefined
    return selQuestionnaireNavState(s.questionnaire, { stFolder: s })
  })
  const progress = useSelect('stFolder', (s) => {
    if (s.status != 'ready' || s.questionnaire.status != 'ready') return Progress.zero
    return selQuestionnaireProgress(s.questionnaire, { stFolder: s })
  })
  const tabs = useSelect('stFolder', (s) => {
    if (s.status != 'ready' || s.questionnaire.status != 'ready') return []
    return selQuestionnaireTabs(s.questionnaire, { stFolder: s })
  })
  const documentUploads = useProcess('documentUploads')
  const failedUploads = useSelect('documentUploads', selFailedUploads)

  const personalizedNote = useSelect('stFolder', (s) => {
    if (s.status != 'ready' || s.questionnaire.status != 'ready') return undefined
    return selQuestionnairePersonalizedNote(s.questionnaire, { stFolder: s })
  })

  const shouldShowSubtitle = useSelect('stFolder', (s) =>
    currentPage ? selEvalFolderStateExpr(s, currentPage.config.subtitleShowWhen ?? undefined) : false
  )

  const membershipSide = useSelect('stFolder', selMembershipSide)

  const questionnaireValidationResult = useSelect('stFolder', (s): QuestionnairePageValidationResult =>
      s.status == 'ready' && s.questionnaire.status == 'ready'
        ? selQuestionnaireValidationResult(s.questionnaire, { stFolder: s })
        : { ok: true }
  )
  const questionnaireValidationErrorMessage = questionnaireValidationResult.ok
    ? undefined
    : formatValidationErrors(questionnaireValidationResult.errors)

  const documentsCount = useSelect('stFolder', (s) => selUploadedDocuments(s).length)

  const [action, setAction] = useState<QuestionnaireAction | undefined>()
  // If the current page has required-field validation errors, surface that banner first.
  const prioritizedBanner = match({
    questionnaireValidationErrorMessage,
    organizationPreviewNote: currentPage?.organizationPreviewNote
  })
    .with(
      { questionnaireValidationErrorMessage: P.string },
      ({ questionnaireValidationErrorMessage }) => (
        <QuestionnaireBanner variant="error">{questionnaireValidationErrorMessage}</QuestionnaireBanner>
      )
    )
    .with({ organizationPreviewNote: P.string }, ({ organizationPreviewNote }) => (
      <QuestionnaireBanner variant="info">{organizationPreviewNote}</QuestionnaireBanner>
    ))
    .otherwise(() => null)

  if (!folderState) {
    return null
  }

  const folder = folderState.folder
  const organization = folderState.organization

  if (!currentPage || !navState) {
    return null
  }

  const actionDialog = match(action)
    .with({ type: 'viewAttachments' }, () => (
      <Modal isOpen={true}>
        <ClientUploadDocumentsCard variant="dialog" onClose={() => setAction(undefined)} />
      </Modal>
    ))
    .with({ type: 'confirmCompleteQuestionnaire' }, () => (
      <Modal isOpen={true}>
        <Dialog
          title="Complete questionnaire"
          subtitle="This will complete the questionnaire on behalf of your client. If you'd like to close the questionnaire without completing it, use the Close button at the top right of the page."
          buttons={
            <DialogButtons>
              <Button variant="default" onClick={() => setAction(undefined)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setAction(undefined)
                  stFolder.send({
                    type: 'questionnaire',
                    message: { type: 'markQuestionnaireAsCompleted' }
                  })
                }}
              >
                Complete questionnaire
              </Button>
            </DialogButtons>
          }
        />
      </Modal>
    ))
    .otherwise(() => null)

  return (
    <QuestionnairePageContainer
      banner={prioritizedBanner}
      header={
        <QuestionnaireHeader
          leading={<div className="text-md hidden font-semibold md:block">{organization.name}</div>}
          trailing={
            <STQuestionnaireTrailingHeader
              documentsCount={documentsCount}
              onClickViewAttachments={() => setAction({ type: 'viewAttachments' })}
              onClose={() => {
                stFolder.send({ type: 'questionnaire', message: { type: 'returnToDashboard' } })
              }}
            />
          }
        >
          {tabs.map((tab) => {
            return (
              <QuestionnaireSectionTab
                key={tab.route.sectionId}
                icon={tab.route.sectionId}
                caption={tab.label}
                selected={tab.route.sectionId == navState.cur.sectionId}
                onClick={() =>
                  stFolder.send({ type: 'questionnaire', message: { type: 'navigate', route: tab.route } })
                }
              />
            )
          })}
        </QuestionnaireHeader>
      }
      stickyNote={
        personalizedNote && (
          <div className="lg:sticky lg:left-6 lg:top-6 lg:h-0 lg:w-72">
            <PostItNote className="w-full" signature={organization.name!}>
              {personalizedNote.note}
            </PostItNote>
          </div>
        )
      }
      footer={
        <QuestionnaireFooter>
          <Button
            size="l"
            variant="primary"
            leadingIcon={<ArrowLeftIcon className="h-4 w-4" />}
            disabled={navState.prev == undefined}
            onClick={() => {
              if (!navState.prev) return
              stFolder.send({
                type: 'questionnaire',
                message: { type: 'navigate', route: navState.prev }
              })
            }}
          >
            Back
          </Button>
          <QuestionnaireProgressBar value={Progress.toNumber(progress)} />
          {match(navState.next)
            .with(P.nullish, () => (
              <Button
                size="l"
                variant="primary"
                onClick={() => {
                  // We don't want to have someone on the organization (firm) side accidentally
                  // complete the questionnaire on behalf of the client.
                  //
                  // So we show a confirmation dialog to someone on the firm side.
                  if (membershipSide == 'firm') {
                    setAction({ type: 'confirmCompleteQuestionnaire' })
                  } else {
                    stFolder.send({
                      type: 'questionnaire',
                      message: { type: 'markQuestionnaireAsCompleted' }
                    })
                  }
                }}
              >
                Complete questionnaire
              </Button>
            ))
            .otherwise(() => (
              <Button
                size="l"
                variant="primary"
                trailingIcon={<ArrowRightIcon className="h-4 w-4" />}
                disabled={navState.next == undefined}
                onClick={() => stFolder.send({ type: 'questionnaire', message: { type: 'navigateNext' } })}
              >
                Next
              </Button>
            ))}
        </QuestionnaireFooter>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex w-full flex-col gap-2 py-2">
          {currentPage.config.title ? (
            <h1 className="text-center text-3xl font-bold">{currentPage.config.title}</h1>
          ) : null}

          {currentPage.config.subtitle && shouldShowSubtitle ? (
            <p className="text-center">
              <SimpleHtmlMarkdown content={currentPage.config.subtitle} />
            </p>
          ) : null}
        </div>

        {currentPage.config.components.map((i, index) => (
          <ConnectedWizardComponent
            key={index}
            config={i}
            validationResult={questionnaireValidationResult}
          />
        ))}
      </div>

      {actionDialog}

      {failedUploads.length > 0 && (
        <Modal isOpen={true}>
          <UploadFailedDialog
            failedUpload={failedUploads[0]}
            onClose={() =>
              documentUploads.send({ type: 'clearUpload', uploadId: failedUploads[0].id })
            }
          />
        </Modal>
      )}
    </QuestionnairePageContainer>
  )
}

function UploadFailedDialog({
  failedUpload,
  onClose
}: {
  failedUpload: FailedUpload
  onClose: () => void
}) {
  return (
    <Dialog
      title="Upload failed"
      subtitle={
        <div>
          <p>
            The file <span className="font-bold">{failedUpload.filename}</span> failed:{' '}
            {formatFailedUpload(failedUpload)}.
          </p>
          <p>Please try to upload the file again.</p>
        </div>
      }
      buttons={
        <DialogButtons>
          <Button variant="primary" onClick={onClose}>
            OK
          </Button>
        </DialogButtons>
      }
    />
  )
}

function formatFailedUpload(upload: FailedUpload) {
  switch (upload.error) {
    case 'empty_file':
      return 'file is empty'
    case 'network_failure':
      return 'network error'
    case 'rejected':
      return 'upload failed'
  }
}
